import React, { useState, useEffect, useCallback } from "react";
import { DateTime } from "luxon";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid2 as Grid,
  Stack,
  Link,
  Card,
  Divider,
  CardActions,
  CardContent,
  IconButton,
  Chip,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle,
  AccessTime,
  Person,
  LocationOn,
  Speed,
  Schedule,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

function RealTimeRequestModal({
  open,
  onClose,
  selectedJob,
  currentUser,
  onAccept,
  rejectRequestHandler,
}) {
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const utcDate = DateTime.fromISO(dateString, { zone: "utc" });
    const localDate = utcDate.setZone(currentUser?.timezone);
    return localDate.toFormat("MMMM dd, yyyy hh:mm a");
  };

  const getRequestTypeInfo = () => {
    const isImmediate = selectedJob?.type === "immediate";
    return {
      type: isImmediate ? "Expedited Request" : "Later Date Request",
      color: isImmediate ? "error" : "warning",
      icon: isImmediate ? <Speed /> : <Schedule />,
      description: isImmediate
        ? "Customer needs immediate notarization service"
        : "Customer is requesting a scheduled notarization appointment",
    };
  };

  const calculateTimeRemaining = useCallback(() => {
    if (!selectedJob?.created_at) return 0;

    const createdAt = DateTime.fromISO(selectedJob.created_at, { zone: "utc" });
    const expiryTime = createdAt.plus({ minutes: 5 });
    const now = DateTime.utc();
    const timeLeft = expiryTime.diff(now);

    return Math.max(0, timeLeft.milliseconds);
  }, [selectedJob?.created_at]);

  useEffect(() => {
    if (!open || !selectedJob) return;

    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      // Don't auto-close the modal when viewing details
      // Users can manually close it if they want
    }, 1000);

    return () => clearInterval(interval);
  }, [open, selectedJob, calculateTimeRemaining]);

  const formatTimeRemaining = (milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const getProgressValue = () => {
    const totalTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    return Math.max(0, (timeRemaining / totalTime) * 100);
  };

  const handleAcceptRequest = async () => {
    setAccepting(true);
    try {
      await onAccept(selectedJob.id);
      setSuccess("Request accepted successfully!");
      setConfirmDialogOpen(false);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setError(error.message || "Failed to accept request");
    } finally {
      setAccepting(false);
    }
  };

  const requestTypeInfo = getRequestTypeInfo();

  if (!selectedJob) return null;

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", md: "80%", lg: "70%" },
            maxWidth: "900px",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "hidden",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 10, top: 10, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>

          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              maxHeight: "90vh",
              overflow: "hidden",
            }}
          >
            <CardContent
              sx={{
                p: 4,
                flex: 1,
                overflow: "auto",
                minHeight: 0,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <Typography variant="h4" fontWeight="bold">
                  Real-Time Notarization Request
                </Typography>
              </Box>

              {/* Time Remaining Alert */}
              {timeRemaining > 0 && (
                <Alert
                  severity={timeRemaining < 60000 ? "error" : "warning"}
                  sx={{ mb: 3 }}
                  icon={<AccessTime />}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Time Remaining: {formatTimeRemaining(timeRemaining)}
                    </Typography>
                    <Typography variant="body2">
                      This request will expire automatically if not accepted
                      within the time limit.
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={getProgressValue()}
                      sx={{ mt: 1, height: 8, borderRadius: 4 }}
                      color={timeRemaining < 60000 ? "error" : "warning"}
                    />
                  </Box>
                </Alert>
              )}

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert
                  severity="success"
                  sx={{ mb: 2 }}
                  onClose={() => setSuccess("")}
                >
                  {success}
                </Alert>
              )}

              <Grid container spacing={3}>
                {/* Request Information */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <AccessTime sx={{ mr: 1, verticalAlign: "middle" }} />
                        Request Details
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Request Time
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDateTime(selectedJob.created_at)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Request Type
                          </Typography>
                          <Chip
                            label={requestTypeInfo.type}
                            color={requestTypeInfo.color}
                            size="small"
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Document Type
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob.document?.type || "Permit Package"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Priority
                          </Typography>
                          <Chip
                            label={
                              selectedJob.type === "immediate"
                                ? "HIGH"
                                : "NORMAL"
                            }
                            color={
                              selectedJob.type === "immediate"
                                ? "error"
                                : "primary"
                            }
                            size="small"
                          />
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Requester Information */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <Person sx={{ mr: 1, verticalAlign: "middle" }} />
                        Requester Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Full Name
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedJob.requester?.fullName || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Email Address
                          </Typography>
                          <Link
                            href={`mailto:${selectedJob.requester?.email}`}
                            color="inherit"
                          >
                            <Typography variant="body2">
                              {selectedJob.requester?.email || "N/A"}
                            </Typography>
                          </Link>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Phone Number
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob.requester?.phone || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Location
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob.requester?.city || "N/A"},{" "}
                            {selectedJob.requester?.state || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Project Information */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <LocationOn sx={{ mr: 1, verticalAlign: "middle" }} />
                        Project Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Property Address
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedJob.project_address || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Owner
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob.document?.owner || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            County
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob.document?.county || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Estimated Duration
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob.estimated_duration || "30-45 minutes"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Additional Information */}
                <Grid size={{ xs: 12 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Important Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          {requestTypeInfo.description}
                        </Typography>
                      </Alert>

                      {selectedJob.type === "immediate" && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="bold">
                            Expedited Request: The customer is waiting and ready
                            to begin the notarization process immediately.
                          </Typography>
                        </Alert>
                      )}

                      {selectedJob.special_requirements && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            gutterBottom
                          >
                            Special Requirements
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob.special_requirements}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>

            <CardActions
              sx={{
                p: 3,
                pt: 2,
                position: "sticky",
                bottom: 0,
                backgroundColor: "background.paper",
                borderTop: "1px solid",
                borderColor: "divider",
                zIndex: 10,
                boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                width="100%"
                justifyContent="flex-end"
              >
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => {
                    onClose();
                  }}
                  disabled={accepting}
                >
                  Close
                </Button>

                <LoadingButton
                  startIcon={<CheckCircle />}
                  variant="contained"
                  color="success"
                  onClick={() => setConfirmDialogOpen(true)}
                  loading={accepting}
                  size="large"
                  disabled={timeRemaining <= 0}
                >
                  Accept Request
                </LoadingButton>
              </Stack>
            </CardActions>
          </Card>
        </Box>
      </Modal>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Request Acceptance</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              You are about to accept this {requestTypeInfo.type.toLowerCase()}!
            </Typography>
          </Alert>

          <Typography variant="body2" sx={{ mb: 2 }}>
            By accepting this request, you confirm that:
          </Typography>

          <ul style={{ margin: 0, paddingLeft: "20px" }}>
            <li>
              <Typography variant="body2">
                You are available to complete this notarization
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                You will provide professional notary services according to your
                state regulations
              </Typography>
            </li>
            {selectedJob.type === "immediate" && (
              <li>
                <Typography variant="body2" color="error">
                  You can begin the notarization process immediately
                </Typography>
              </li>
            )}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={accepting}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleAcceptRequest}
            loading={accepting}
            variant="contained"
            color="primary"
            startIcon={<CheckCircle />}
          >
            Yes, Accept Request
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RealTimeRequestModal;
